import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Info, Loader2, Timer } from 'lucide-react';
import { HOLD_SECONDS } from './PanicButton';

/**
 * Every outcome the UI can be in. Owned here because this panel is what turns each
 * one into words, and App only has to pick the variant.
 */
export type PanicState =
  | { kind: 'ready' }
  | { kind: 'charging' }
  | { kind: 'sending' }
  /** `responderTeams` is empty when the API had no team to route the alert to. */
  | { kind: 'sent'; alertId: string | null; responderTeams: string[] }
  /** `until` is an absolute epoch ms deadline, so the countdown survives a slow render. */
  | { kind: 'cooldown'; until: number; alertId: string | null }
  | { kind: 'error'; message: string; recover: 'retry' | 'sign_in_again' };

export interface StatusPanelProps {
  state: PanicState;
  onRetry: () => void;
  onSignInAgain: () => void;
  /** Arms the button again after a successful page, for a genuinely new incident. */
  onReset: () => void;
  /** Fires once the cooldown deadline passes, so App can return to ready. */
  onCooldownExpired: () => void;
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Tone carries an icon and a title as well as a color, never color on its own. */
const TONES = {
  quiet: 'border-border bg-card text-foreground',
  active: 'border-primary/40 bg-primary/5 text-foreground',
  good: 'border-success-border bg-success-bg text-success-fg',
  wait: 'border-warning-border bg-warning-bg text-warning-fg',
  bad: 'border-danger-border bg-danger-bg text-danger-fg',
} as const;

/**
 * Entrance per tone, so the motion carries the same information the colour does.
 * A confirmed page pops, a failure nudges, everything else just settles in. All
 * motion-safe, because none of it is load bearing: the icon and the title say the
 * same thing to someone who has turned animation off.
 */
const MOTION: Record<keyof typeof TONES, string> = {
  quiet: 'motion-safe:animate-fade-in',
  active: 'motion-safe:animate-fade-in-up',
  good: 'motion-safe:animate-pop-in',
  wait: 'motion-safe:animate-fade-in-up',
  bad: 'motion-safe:animate-nudge',
};

export function StatusPanel({
  state,
  onRetry,
  onSignInAgain,
  onReset,
  onCooldownExpired,
}: StatusPanelProps) {
  const until = state.kind === 'cooldown' ? state.until : null;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (until === null) return;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [until]);

  const remainingSeconds = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));

  useEffect(() => {
    if (until !== null && remainingSeconds === 0) onCooldownExpired();
  }, [until, remainingSeconds, onCooldownExpired]);

  let tone: keyof typeof TONES = 'quiet';
  let icon: ReactNode = <Info className="size-5 shrink-0" aria-hidden="true" />;
  let title = '';
  let body: ReactNode = null;

  switch (state.kind) {
    case 'ready':
      tone = 'quiet';
      icon = <Info className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />;
      title = 'Listo para presionar';
      body = (
        // These headcounts are written by hand and describe the OpsGenie rotation,
        // not anything this code can read. If the schedules change, this sentence
        // becomes a lie about how many people get woken, so it needs updating with
        // them.
        //
        // As of 2026-08-05, Vertex Escalation notifies at 0 minutes:
        //   - Vertex DevOps Schedule, a WEEKLY rotation between 2 people, so
        //     exactly ONE DevOps engineer is on call at a time. Hence "el DevOps de
        //     guardia", singular. An earlier draft said "2 DevOps", which overstated
        //     it by one phone.
        //   - Vertex Devs Schedule, a weekly rotation between dev team 1 and dev
        //     team 2, so one team of ~5, never both.
        // Then 4 named leads at 10 minutes, and both dev teams in full at 25.
        // Check with: GET /v2/escalations/Vertex Escalation?identifierType=name
        <p className="text-sm text-muted-foreground">
          Disparar esta alerta notifica de manera <strong className="font-semibold">urgente</strong> a
          un grupo de 5 desarrolladores, más el DevOps de guardia. En caso que nadie lea la alerta, se
          notifica a líderes técnicos.
        </p>
      );
      break;

    case 'charging':
      tone = 'active';
      icon = <Timer className="size-5 shrink-0 text-primary" aria-hidden="true" />;
      title = 'Cargando';
      body = (
        <p className="text-sm text-muted-foreground">
          Seguí apretando. Si soltás ahora no se envía ninguna alerta.
        </p>
      );
      break;

    case 'sending':
      tone = 'active';
      icon = <Loader2 className="size-5 shrink-0 animate-spin text-primary" aria-hidden="true" />;
      title = 'Llamando a guardia';
      body = <p className="text-sm text-muted-foreground">Creando la alerta en OpsGenie.</p>;
      break;

    case 'sent':
      tone = 'good';
      icon = <CheckCircle2 className="size-5 shrink-0" aria-hidden="true" />;
      title = 'El ingeniero de guardia fue notificado';
      body = (
        <>
          {/* No alert id here. It is a 36 character UUID and, on the success path,
              nothing anyone needs: the sender's next action is to wait or to phone,
              neither of which requires an OpsGenie id. It is still shown in the
              cooldown state, where it identifies WHICH alert is already open. */}
          {/* No responder team means OpsGenie was handed an alert with nobody
              attached to it. It may still route on the integration's own rules, or
              it may sit there notifying nobody, and this page cannot tell which.
              Saying so beats a green tick that means "created", not "answered". */}
          {state.responderTeams.length === 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-sm font-semibold">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>
                Este despliegue no tiene equipos de guardia configurados, así que la alerta puede no
                haber notificado a nadie. Confirmá en OpsGenie, o llamá por teléfono al ingeniero de
                guardia, y avisale al equipo técnico que falta OPSGENIE_TEAM.
              </span>
            </p>
          )}
          {/* Without this the button is stuck for the rest of the session, and a
              second, unrelated outage would need a page reload. The server owns the
              real cooldown, so a deliberate second attempt is safe to offer. */}
          <button
            type="button"
            onClick={onReset}
            className="mt-3 inline-flex h-11 items-center justify-center rounded-lg border border-success-border bg-background px-4 text-sm font-semibold text-success-fg transition-colors hover:bg-success-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-success-border focus-visible:ring-offset-2 focus-visible:ring-offset-success-bg"
          >
            Hay otro problema, reactivar el botón
          </button>
        </>
      );
      break;

    case 'cooldown':
      tone = 'wait';
      icon = <Clock className="size-5 shrink-0" aria-hidden="true" />;
      title = 'Ya hay una alerta abierta';
      body = (
        <>
          <p className="text-sm">
            Alguien ya llamó a guardia hace poco, así que puede que ya estén trabajando en esto.
          </p>
          {/* The bypass, stated plainly. The button above stays usable on purpose:
              the sender may know about something the open alert does not cover, and
              refusing them for another few minutes is the wrong default for a panic
              button. Saying it here is what makes the second hold a decision rather
              than a surprise. */}
          <p className="mt-2 text-sm font-semibold">
            Si es otro problema, o nadie te contactó, mantené el botón {HOLD_SECONDS} segundos otra
            vez y se envía igual.
          </p>
          <p className="mt-2 text-sm">
            El aviso automático se libera solo en{' '}
            <span className="font-mono tabular-nums">{formatCountdown(remainingSeconds)}</span>.
          </p>
        </>
      );
      break;

    case 'error':
      tone = 'bad';
      icon = <AlertTriangle className="size-5 shrink-0" aria-hidden="true" />;
      title = 'La alerta no se envió';
      body = (
        <>
          <p className="text-sm">{state.message}</p>
          {/* Never a dead end. The phone number is the fallback that does not depend
              on this page, on Google, or on OpsGenie's API being up. */}
          <p className="mt-2 text-sm">
            Probá de nuevo, y si falla una segunda vez, llamá por teléfono al ingeniero de guardia.
            Ese camino no depende de esta página.
          </p>
          <button
            type="button"
            onClick={state.recover === 'retry' ? onRetry : onSignInAgain}
            className="mt-3 inline-flex h-11 items-center justify-center rounded-lg border border-danger-border bg-background px-4 text-sm font-semibold text-danger-fg transition-colors hover:bg-danger-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-border focus-visible:ring-offset-2 focus-visible:ring-offset-danger-bg"
          >
            {state.recover === 'retry' ? 'Probar de nuevo' : 'Iniciar sesión de nuevo'}
          </button>
        </>
      );
      break;
  }

  return (
    <section
      // key on the state kind so React remounts the section on every transition,
      // which is what restarts the entrance animation. Without it the panel swaps
      // its text silently and a new outcome is easy to miss.
      key={state.kind}
      // Outcomes are announced here. The button's own live region handles the hold
      // gesture, so the two never describe the same thing.
      role="status"
      aria-live="polite"
      className={`w-full rounded-lg border p-4 ${TONES[tone]} ${MOTION[tone]}`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5">{icon}</span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-bold leading-snug">{title}</h2>
          <div className="mt-1">{body}</div>
        </div>
      </div>
    </section>
  );
}
